import React, { useState, useEffect } from 'react';
import { Calendar, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { whatsappNumber } from '../data/mock';
import { toast } from 'sonner';
import PassengerSelector from './booking/PassengerSelector';
import PriceDisplay from './booking/PriceDisplay';
import CustomerDetailsModal from './booking/CustomerDetailsModal';
import LocationAutocomplete from './booking/LocationAutocomplete';
import QuickDestinations from './booking/QuickDestinations';

const BookingSearch = () => {
  const { language, t } = useLanguage();
  const [searchData, setSearchData] = useState({
    from: null,  // Now stores {label, lat, lon, ...}
    to: null,
    date: '',
    time: '12:00',
    adults: 1,
    children: 0,
    infants: 0
  });

  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [showPassengers, setShowPassengers] = useState(false);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);

  // Calculate price dynamically when route or time changes
  useEffect(() => {
    if (searchData.from && searchData.to && searchData.from.lat && searchData.to.lat) {
      calculatePrice();
    } else {
      setPriceData(null);
    }
  }, [searchData.from, searchData.to, searchData.time]);

  // Listen for destination clicks to pre-fill form (from PopularDestinations)
  useEffect(() => {
    const handleFillForm = (event) => {
      const { from, to } = event.detail;
      // Convert old format to new format
      const predefinedLocations = {
        'turin-city': { label: 'Torino Centro', lat: 45.0703, lon: 7.6869 },
        'milan': { label: 'Milano', lat: 45.4642, lon: 9.1900 },
        'turin-airport': { label: 'Aeroporto di Torino Caselle', lat: 45.2008, lon: 7.6497 }
      };
      
      setSearchData(prev => ({
        ...prev,
        from: predefinedLocations[from] || null,
        to: predefinedLocations[to] || null
      }));
    };

    window.addEventListener('fillBookingForm', handleFillForm);
    return () => window.removeEventListener('fillBookingForm', handleFillForm);
  }, []);

  const calculatePrice = async () => {
    setLoadingPrice(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calculate-price-custom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_lat: searchData.from.lat,
          from_lon: searchData.from.lon,
          to_lat: searchData.to.lat,
          to_lon: searchData.to.lon,
          from_address: searchData.from.label,
          to_address: searchData.to.label,
          time: searchData.time
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPriceData(data);
      }
    } catch (error) {
      console.error('Error calculating price:', error);
    } finally {
      setLoadingPrice(false);
    }
  };

  const handleSearch = async () => {
    // Validate main fields
    if (!searchData.from || !searchData.to) {
      toast.error(language === 'en' ? 'Please select pickup and destination' : 'Seleziona località di partenza e destinazione');
      return;
    }
    
    if (!searchData.date) {
      toast.error(language === 'en' ? 'Please select a date' : 'Seleziona una data');
      return;
    }

    // Check if customer data is already filled
    if (!customerData.name || !customerData.phone) {
      setShowCustomerForm(true);
      return;
    }

    // Submit booking
    await submitBooking();
  };

  const submitBooking = async () => {
    setIsSubmitting(true);
    
    try {
      const bookingData = {
        from_location: searchData.from.label,
        to_location: searchData.to.label,
        date: searchData.date,
        time: searchData.time,
        adults: searchData.adults,
        children: searchData.children,
        infants: searchData.infants,
        customer_name: customerData.name,
        customer_email: customerData.email || 'noreply@rentwithdriver.com',
        customer_phone: customerData.phone
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const message = language === 'en' 
          ? `Booking request sent! From ${searchData.from.label} to ${searchData.to.label} on ${searchData.date}. We'll contact you via WhatsApp shortly.`
          : `Richiesta di prenotazione inviata! Da ${searchData.from.label} a ${searchData.to.label} il ${searchData.date}. Ti contatteremo presto su WhatsApp.`;
        
        const whatsappMessage = encodeURIComponent(
          `🚗 Nuova Prenotazione\n` +
          `📍 Da: ${searchData.from.label}\n` +
          `📍 A: ${searchData.to.label}\n` +
          `📅 Data: ${searchData.date}\n` +
          `🕐 Ora: ${searchData.time}\n` +
          `👥 Passeggeri: ${searchData.adults} adulti, ${searchData.children} bambini, ${searchData.infants} neonati\n` +
          `💰 Prezzo stimato: €${priceData?.final_price || 'Da calcolare'}\n` +
          `\n👤 ${customerData.name}\n` +
          `📞 ${customerData.phone}\n` +
          `📧 ${customerData.email || 'N/A'}`
        );
        
        toast.success(message);
        
        // Redirect to WhatsApp
        setTimeout(() => {
          window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
        }, 1500);
        
        // Reset form
        setSearchData({
          from: null,
          to: null,
          date: '',
          time: '12:00',
          adults: 1,
          children: 0,
          infants: 0
        });
        setCustomerData({ name: '', email: '', phone: '' });
        setShowCustomerForm(false);
        setPriceData(null);
      } else {
        const errorData = await response.json();
        console.error('Booking error:', errorData);
        toast.error(language === 'en' ? 'Booking failed. Please try again.' : 'Prenotazione fallita. Riprova.');
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error(language === 'en' ? 'Connection error. Please try again.' : 'Errore di connessione. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCustomerSubmit = () => {
    if (customerData.name && customerData.phone) {
      setShowCustomerForm(false);
      submitBooking();
    } else {
      toast.error(language === 'en' ? 'Please fill in all required fields' : 'Compila tutti i campi obbligatori');
    }
  };

  const handleQuickDestinationSelect = (dest, isFrom = true) => {
    const locationData = {
      label: language === 'en' ? dest.labelEn : dest.labelIt,
      lat: dest.lat,
      lon: dest.lon,
      name: language === 'en' ? dest.labelEn : dest.labelIt
    };

    if (isFrom) {
      setSearchData(prev => ({ ...prev, from: locationData }));
    } else {
      setSearchData(prev => ({ ...prev, to: locationData }));
    }
  };

  return (
    <section id="booking-form" className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4">
        <Card className="max-w-5xl mx-auto p-8 shadow-2xl bg-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {language === 'en' ? 'Book Your Premium Transfer' : 'Prenota il Tuo Trasferimento Premium'}
            </h2>
            <p className="text-gray-600">
              {language === 'en' ? 'Enter any address and get instant pricing' : 'Inserisci qualsiasi indirizzo e ottieni il prezzo istantaneo'}
            </p>
          </div>

          {/* Quick Destinations */}
          <QuickDestinations 
            onSelect={(dest) => handleQuickDestinationSelect(dest, !searchData.from)} 
            language={language}
          />

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* From - Autocomplete */}
            <LocationAutocomplete
              value={searchData.from}
              onChange={(location) => setSearchData({ ...searchData, from: location })}
              placeholder={language === 'en' ? 'Enter pickup address...' : 'Inserisci indirizzo di partenza...'}
              label={language === 'en' ? 'From' : 'Da'}
              testId="booking-from-input"
              language={language}
            />

            {/* To - Autocomplete */}
            <LocationAutocomplete
              value={searchData.to}
              onChange={(location) => setSearchData({ ...searchData, to: location })}
              placeholder={language === 'en' ? 'Enter destination address...' : 'Inserisci indirizzo di destinazione...'}
              label={language === 'en' ? 'To' : 'A'}
              testId="booking-to-input"
              language={language}
            />

            {/* Date & Time */}
            <div className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                {language === 'en' ? 'Date & Time' : 'Data & Ora'}
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={searchData.date}
                  onChange={(e) => setSearchData({ ...searchData, date: e.target.value })}
                  data-testid="booking-date-input"
                  className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:outline-none text-gray-800"
                  min={new Date().toISOString().split('T')[0]}
                />
                <input
                  type="time"
                  value={searchData.time}
                  onChange={(e) => setSearchData({ ...searchData, time: e.target.value })}
                  data-testid="booking-time-input"
                  className="w-24 px-2 py-3 rounded-lg border-2 border-gray-200 focus:border-amber-500 focus:outline-none text-gray-800"
                />
              </div>
            </div>

            {/* Passengers */}
            <PassengerSelector
              searchData={searchData}
              setSearchData={setSearchData}
              showPassengers={showPassengers}
              setShowPassengers={setShowPassengers}
              language={language}
            />
          </div>

          {/* Search Button & Price Display */}
          <div className="mt-6 flex flex-col gap-4">
            <PriceDisplay 
              priceData={priceData} 
              loadingPrice={loadingPrice} 
              language={language} 
            />

            <Button
              onClick={handleSearch}
              disabled={isSubmitting}
              data-testid="booking-submit-btn"
              className="w-full md:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold text-lg px-12 py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Search className="w-5 h-5 mr-2" />
              {isSubmitting 
                ? (language === 'en' ? 'Saving...' : 'Salvataggio...') 
                : (language === 'en' ? 'Search & Book' : 'Cerca & Prenota')
              }
            </Button>
          </div>

          {/* Customer Details Modal */}
          <CustomerDetailsModal
            show={showCustomerForm}
            customerData={customerData}
            setCustomerData={setCustomerData}
            onCancel={() => setShowCustomerForm(false)}
            onSubmit={handleCustomerSubmit}
            language={language}
          />
        </Card>
      </div>
    </section>
  );
};

export default BookingSearch;
